import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuthContext } from '@/providers/Auth';
import { refUrlService } from '@/features/auth';
import { campaignApiService } from '../services/campaignApi';

// Reports the stored affiliate/CPS landing URL for already-authenticated
// users. New users are attributed via the signUp request instead — that
// flow clears the storage before auth state flips, so this never
// double-reports a fresh registration.
export const useCampaignTracking = (): void => {
  const authContext = useAuthContext();
  const hasTrackedRef = useRef(false);

  const trackMutation = useMutation({
    mutationFn: (refUrl: string) => campaignApiService.track({ refUrl }),
    // Keep the URL on failure so the next visit retries
    onSuccess: () => refUrlService.clear(),
  });

  // isAuthenticated alone only means a Firebase session exists in the
  // browser (possibly stale). user is set only after the backend confirmed
  // the identity (getUser fetch or signup), so gate on it to guarantee the
  // track request is sent exclusively for authorized users.
  const isReady =
    !!authContext &&
    !authContext.isLoading &&
    authContext.isAuthenticated &&
    !!authContext.user;
  const { mutate: track } = trackMutation;

  useEffect(() => {
    if (!isReady || hasTrackedRef.current) {
      return;
    }

    const refUrl = refUrlService.get();
    if (!refUrl) {
      return;
    }

    hasTrackedRef.current = true;
    track(refUrl);
  }, [isReady, track]);
};
